import { PERMISSION_ENTITY_PAYMENT } from '@klear/constants'
import { UPLOAD_ACCEPTED_LABEL, UPLOAD_ACCEPT_ATTRIBUTE, isUploadContentType } from '@klear/upload/upload-formats'
import { DEFAULT_CURRENCY_CODE, type CurrencyCode } from '@klear/constants/currency'
import { KlearDate } from '@klear/date'
import { Money } from '@klear/money'
import type { Payment } from '@klear/payment/payment'
import type { SupplierExternalBankAccount } from '@klear/supplier-external-bank-account'
import type { KlearFinancialAccount } from '@klear/klear-financial-account/klear-financial-account'
import { PAYMENT_NETWORK_ACH, PAYMENT_NETWORK_WIRE } from '@klear/bank-account/constants'
import {
  PAYMENT_ACCOUNT_TYPE_EXTERNAL_FINANCIAL_ACCOUNT,
  PAYMENT_ACCOUNT_TYPE_KLEAR_FINANCIAL_ACCOUNT,
  PAYMENT_ACCOUNT_TYPE_PLACEHOLDER,
  PAYMENT_NETWORK_KLEAR_TO_KLEAR,
  PAYMENT_TYPE_SUPPLIER,
  PaymentNetworkOptions,
  type PaymentAccountType,
  type PaymentNetwork
} from '@klear/payment/constants'
import { Combobox } from '@klear/ui/components/combobox'
import { MoneyInput } from '@klear/ui/components/money-input'
import { DatePicker } from '@klear/ui/components/date-picker'
import { Input } from '@klear/ui/components/input'
import { Label } from '@klear/ui/components/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@klear/ui/components/select'
import { Switch } from '@klear/ui/components/switch'
import { Textarea } from '@klear/ui/components/textarea'
import { klearAccountOptionLabel } from '@klear/ui/hooks/use-klear-financial-account'
import isError from 'lodash/isError'
import isNumber from 'lodash/isNumber'
import noop from 'lodash/noop'
import partition from 'lodash/partition'
import toInteger from 'lodash/toInteger'
import toString from 'lodash/toString'
import values from 'lodash/values'
import type { Draft } from 'immer'
import { useState, type ReactNode } from 'react'
import { Badge } from '@klear/ui/components/badge'
import { Button } from '@klear/ui/components/button'
import { useFileDownload } from '@klear/ui/lib/download-file'
import { X } from 'lucide-react'
import { PaymentReferenceInput, validatePaymentReferenceInput } from '@klear/ui/components/payment-reference-input'
import type { BankPaymentNetwork } from '@klear/bank-account/constants'
import type { PayablePaymentInput } from '@klear/payable/payable.wire'
import { NOTIFY_SUPPLIER_BY_DEFAULT } from '@klear/payable/payment-input'

export type PaymentStepForm = {
  type: typeof PERMISSION_ENTITY_PAYMENT
  number: string
  amount: Money
  effectiveDate: KlearDate
  notes: string
  statementDescriptor: string
  notifySupplier: boolean
  paymentType: typeof PAYMENT_TYPE_SUPPLIER
  paymentNetwork: PaymentNetwork
  sourceAccountType: PaymentAccountType
  sourceAccountId: number | null
  sourceAccountOrganizationId: number | null
  sourceAccountOrganizationName: string
  destinationAccountType: PaymentAccountType
  destinationAccountId: number | null
  destinationAccountOrganizationId: number | null
  destinationAccountOrganizationName: string
}

export type PaymentStepFormErrorMap = Record<string, string | undefined>
export type PaymentStepFormUpdate = (recipe: (draft: Draft<PaymentStepForm>) => void) => void

export type PaymentStepFieldChange = <K extends keyof PaymentStepForm>(field: K, value: PaymentStepForm[K]) => void

type SupplierPaymentNetwork = typeof PAYMENT_NETWORK_ACH | typeof PAYMENT_NETWORK_WIRE

function supplierAccountsForNetwork(
  accounts: SupplierExternalBankAccount[],
  network: SupplierPaymentNetwork
): SupplierExternalBankAccount[] {
  return accounts.filter((account) => account.id != null && account.supportedPaymentNetworks.includes(network))
}

function defaultPaymentNetworkForAccounts(accounts: SupplierExternalBankAccount[]): PaymentNetwork {
  if (supplierAccountsForNetwork(accounts, PAYMENT_NETWORK_WIRE).length > 0) return PAYMENT_NETWORK_WIRE
  if (supplierAccountsForNetwork(accounts, PAYMENT_NETWORK_ACH).length > 0) return PAYMENT_NETWORK_ACH
  return PAYMENT_NETWORK_WIRE
}

export function reconcilePaymentStepFormAccounts(
  form: PaymentStepForm,
  defaults: PaymentStepForm,
  externalBankAccounts: SupplierExternalBankAccount[]
): PaymentStepForm {
  if (externalBankAccounts.length === 0) return form
  if (form.destinationAccountType === PAYMENT_ACCOUNT_TYPE_PLACEHOLDER) return form

  const network = form.paymentNetwork
  if (!network || network === PAYMENT_NETWORK_KLEAR_TO_KLEAR) return form

  const matching = supplierAccountsForNetwork(externalBankAccounts, network)
  if (matching.length > 0) {
    if (form.destinationAccountId != null && matching.some((account) => account.id === form.destinationAccountId)) {
      return form
    }
    const first = matching[0]
    if (first == null) return form
    return {
      ...form,
      destinationAccountId: first.id ?? null,
      destinationAccountOrganizationId: first.organizationId,
      destinationAccountOrganizationName: ''
    }
  }

  return {
    ...form,
    paymentNetwork: defaults.paymentNetwork,
    destinationAccountId: defaults.destinationAccountId,
    destinationAccountOrganizationId: defaults.destinationAccountOrganizationId,
    destinationAccountOrganizationName: defaults.destinationAccountOrganizationName
  }
}

export function paymentStepFormDefaults(opts: {
  parentAmount?: Money
  parentCurrency?: CurrencyCode
  externalBankAccounts: SupplierExternalBankAccount[]
  klearFinancialAccount?: KlearFinancialAccount | null
}): PaymentStepForm {
  const paymentNetwork = defaultPaymentNetworkForAccounts(opts.externalBankAccounts)
  const destinationAccount =
    supplierAccountsForNetwork(opts.externalBankAccounts, paymentNetwork as SupplierPaymentNetwork)[0] ?? null

  return {
    type: PERMISSION_ENTITY_PAYMENT,
    number: '',
    amount: opts.parentAmount ?? Money.zero(opts.parentCurrency ?? DEFAULT_CURRENCY_CODE),
    effectiveDate: KlearDate.today(),
    notes: '',
    statementDescriptor: '',
    notifySupplier: NOTIFY_SUPPLIER_BY_DEFAULT,
    paymentType: PAYMENT_TYPE_SUPPLIER,
    paymentNetwork,
    sourceAccountType: PAYMENT_ACCOUNT_TYPE_KLEAR_FINANCIAL_ACCOUNT,
    sourceAccountId: opts.klearFinancialAccount?.id ?? null,
    sourceAccountOrganizationId: opts.klearFinancialAccount?.organization.id ?? null,
    sourceAccountOrganizationName: opts.klearFinancialAccount?.organization.name ?? '',
    destinationAccountType: PAYMENT_ACCOUNT_TYPE_EXTERNAL_FINANCIAL_ACCOUNT,
    destinationAccountId: destinationAccount?.id ?? null,
    destinationAccountOrganizationId: destinationAccount?.organizationId ?? null,
    destinationAccountOrganizationName: ''
  }
}

function chosenAccountId(type: PaymentAccountType, id: number, fallback: number | null): number | null {
  if (type === PAYMENT_ACCOUNT_TYPE_PLACEHOLDER) return null
  return id > 0 ? id : fallback
}

export function paymentStepFormFromPayment(payment: Payment, defaults: PaymentStepForm): PaymentStepForm {
  return {
    type: PERMISSION_ENTITY_PAYMENT,
    number: payment.number ?? defaults.number,
    amount: payment.amount,
    effectiveDate: payment.effectiveDate ?? defaults.effectiveDate,
    notes: payment.notes ?? defaults.notes,
    statementDescriptor: payment.statementDescriptor ?? defaults.statementDescriptor,
    notifySupplier: payment.notifySupplier,
    paymentType: PAYMENT_TYPE_SUPPLIER,
    paymentNetwork: payment.paymentNetwork ?? defaults.paymentNetwork,
    sourceAccountType: payment.sourceAccountType,
    sourceAccountId: chosenAccountId(payment.sourceAccountType, payment.sourceAccountId, defaults.sourceAccountId),
    sourceAccountOrganizationId:
      payment.sourceAccountOrganizationId > 0
        ? payment.sourceAccountOrganizationId
        : defaults.sourceAccountOrganizationId,
    sourceAccountOrganizationName: payment.sourceAccountOrganizationName || defaults.sourceAccountOrganizationName,
    destinationAccountType: payment.destinationAccountType,
    destinationAccountId: chosenAccountId(
      payment.destinationAccountType,
      payment.destinationAccountId,
      defaults.destinationAccountId
    ),
    destinationAccountOrganizationId:
      payment.destinationAccountOrganizationId > 0
        ? payment.destinationAccountOrganizationId
        : defaults.destinationAccountOrganizationId,
    destinationAccountOrganizationName:
      payment.destinationAccountOrganizationName || defaults.destinationAccountOrganizationName
  }
}

export function paymentStepFormFromInput(
  stored: PayablePaymentInput | null,
  defaults: PaymentStepForm
): PaymentStepForm {
  if (stored == null) return defaults

  return {
    ...defaults,
    type: PERMISSION_ENTITY_PAYMENT,
    number: stored.number ?? defaults.number,
    amount: new Money(stored.amount),
    effectiveDate: KlearDate.of(stored.effectiveDate),
    notes: stored.notes ?? defaults.notes,
    statementDescriptor: stored.statementDescriptor ?? defaults.statementDescriptor,
    notifySupplier: stored.notifySupplier,
    paymentType: PAYMENT_TYPE_SUPPLIER,
    paymentNetwork: stored.paymentNetwork,
    sourceAccountType: stored.sourceAccountType,
    sourceAccountId: chosenAccountId(stored.sourceAccountType, stored.sourceAccountId, defaults.sourceAccountId),
    sourceAccountOrganizationId:
      stored.sourceAccountOrganizationId > 0
        ? stored.sourceAccountOrganizationId
        : defaults.sourceAccountOrganizationId,
    destinationAccountType: stored.destinationAccountType,
    destinationAccountId: chosenAccountId(
      stored.destinationAccountType,
      stored.destinationAccountId,
      defaults.destinationAccountId
    ),
    destinationAccountOrganizationId:
      stored.destinationAccountOrganizationId > 0
        ? stored.destinationAccountOrganizationId
        : defaults.destinationAccountOrganizationId
  }
}

export function validatePaymentStepForm(form: PaymentStepForm): PaymentStepFormErrorMap {
  const errors: PaymentStepFormErrorMap = {}

  if (!form.amount.greaterThan(form.amount.zero)) {
    errors.amount = form.amount.isZero()
      ? 'This payable has no amount yet — set an amount on its operational event first.'
      : `This payable totals ${form.amount.toString()}, so there is nothing to send. Its items exceed the events it covers.`
  }
  if (form.sourceAccountId == null || form.sourceAccountId < 1) {
    errors.sourceAccountId = 'Source account is required'
  }
  if (form.destinationAccountId == null || form.destinationAccountId < 1) {
    errors.destinationAccountId = 'Destination account is required'
  }
  if (!form.paymentNetwork) {
    errors.paymentNetwork = 'Network is required'
  }

  if (form.paymentNetwork === PAYMENT_NETWORK_ACH || form.paymentNetwork === PAYMENT_NETWORK_WIRE) {
    const referenceResult = validatePaymentReferenceInput(form.statementDescriptor, form.paymentNetwork)
    if (!referenceResult.ok) {
      errors.statementDescriptor = referenceResult.message
    }
  }

  return errors
}

export function paymentInputFromStepForm(form: PaymentStepForm): PayablePaymentInput {
  const data: PayablePaymentInput = {
    amount: form.amount.toJSON(),
    effectiveDate: form.effectiveDate.toJSON(),
    paymentType: form.paymentType,
    paymentNetwork: form.paymentNetwork,
    sourceAccountType: form.sourceAccountType,
    sourceAccountId: form.sourceAccountId ?? 0,
    sourceAccountOrganizationId: form.sourceAccountOrganizationId ?? 0,
    destinationAccountType: form.destinationAccountType,
    destinationAccountId: form.destinationAccountId ?? 0,
    destinationAccountOrganizationId: form.destinationAccountOrganizationId ?? 0,
    notifySupplier: form.notifySupplier
  }

  const number = form.number.trim()
  if (number.length > 0) {
    data.number = number
  }

  const notes = form.notes.trim()
  if (notes.length > 0) {
    data.notes = notes
  }

  const statementDescriptor = form.statementDescriptor.trim()
  if (statementDescriptor.length > 0) {
    data.statementDescriptor = statementDescriptor
  }

  return data
}

export function hasPaymentStepFormErrors(errors: PaymentStepFormErrorMap): boolean {
  return values(errors).some(Boolean)
}

export function clearPaymentStepFormErrors(
  errors: PaymentStepFormErrorMap,
  paths: readonly (keyof PaymentStepForm | string)[]
): PaymentStepFormErrorMap {
  const next = { ...errors }
  for (const path of paths) {
    delete next[path]
  }
  return next
}

function FieldError({ message }: { message?: string }) {
  return message ? <p className="text-sm text-destructive">{message}</p> : null
}

function amountHelperText(linkedEventCount?: number, itemCount = 0): string {
  const events = linkedEventCount ?? 0
  if (itemCount > 0) {
    const eventPart = events === 1 ? 'the linked operational event' : `the ${events} linked operational events`
    const feePart = itemCount === 1 ? 'one line item' : `${itemCount} items`
    return `Sum of ${eventPart} plus ${feePart}.`
  }
  return events > 1
    ? `Sum of the ${events} linked operational events. Change an event's amount to change this.`
    : "Taken from the linked operational event. Change the event's amount to change this."
}

interface PaymentStepFieldsProps {
  values: PaymentStepForm
  errors: PaymentStepFormErrorMap
  linkedEventCount?: number
  itemCount?: number
  externalBankAccounts: SupplierExternalBankAccount[]
  klearFinancialAccounts?: KlearFinancialAccount[]
  klearFinancialAccount?: KlearFinancialAccount | null
  destinationLocked?: boolean
  onFieldChange: PaymentStepFieldChange
  onFormUpdate: PaymentStepFormUpdate
  receipts?: PaymentReceipt[]
  onUploadReceipts?: (files: File[]) => Promise<void>
  onRemoveReceipt?: (documentId: number) => Promise<void>
  pendingReceipts?: File[]
  onAddPendingReceipts?: (files: File[]) => void
  onRemovePendingReceipt?: (index: number) => void
  getReceiptDownloadUrl?: (documentId: number) => string | null
  createBankAccountAction?: ReactNode
}

export type PaymentReceipt = { id: number; name: string }

export function ReceiptDownloadBadge({ name, url }: { name: string; url: string | null }) {
  const { startDownload, isDownloading } = useFileDownload()

  if (url === null) return <Badge variant="secondary">{name}</Badge>
  return (
    <button type="button" className="inline-flex" disabled={isDownloading} onClick={() => startDownload(url, name)}>
      <Badge variant="secondary" className="cursor-pointer transition-colors hover:bg-blue-50 hover:text-blue-700">
        {name}
      </Badge>
    </button>
  )
}

function ReceiptUploadField({
  receipts,
  pendingReceipts,
  onUploadReceipts,
  onRemoveReceipt,
  onAddPendingReceipts,
  onRemovePendingReceipt,
  getReceiptDownloadUrl
}: {
  receipts: PaymentReceipt[]
  pendingReceipts: File[]
  onUploadReceipts: (files: File[]) => Promise<void>
  onRemoveReceipt?: (documentId: number) => Promise<void>
  onAddPendingReceipts?: (files: File[]) => void
  onRemovePendingReceipt?: (index: number) => void
  getReceiptDownloadUrl?: (documentId: number) => string | null
}) {
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">Document(s)</Label>
      <Input
        type="file"
        multiple
        accept={UPLOAD_ACCEPT_ATTRIBUTE}
        className="cursor-pointer file:cursor-pointer"
        disabled={isUploading}
        onChange={async (e) => {
          const input = e.target
          const [files, refused] = partition([...(input.files ?? [])], (file) => isUploadContentType(file.type))
          const refusedMessage =
            refused.length === 0
              ? null
              : `${refused.map((file) => file.name).join(', ')}: only ${UPLOAD_ACCEPTED_LABEL} files can be attached.`
          setUploadError(refusedMessage)
          if (files.length === 0) {
            input.value = ''
            return
          }
          if (onAddPendingReceipts) {
            onAddPendingReceipts(files)
            input.value = ''
            return
          }
          setIsUploading(true)
          try {
            await onUploadReceipts(files)
          } catch (err: unknown) {
            setUploadError(isError(err) ? err.message : 'Upload failed. Please try again.')
          } finally {
            setIsUploading(false)
            input.value = ''
          }
        }}
      />
      <p className="text-xs text-muted-foreground">{UPLOAD_ACCEPTED_LABEL}</p>
      {isUploading ? <p className="text-sm text-muted-foreground">Uploading…</p> : null}
      <FieldError message={uploadError ?? undefined} />
      {receipts.map((receipt) => (
        <div key={receipt.id} className="flex items-center gap-2">
          {getReceiptDownloadUrl ? (
            <ReceiptDownloadBadge name={receipt.name} url={getReceiptDownloadUrl(receipt.id)} />
          ) : (
            <Badge variant="secondary">{receipt.name}</Badge>
          )}
          {onRemoveReceipt ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() =>
                onRemoveReceipt(receipt.id).catch((err: unknown) =>
                  setUploadError(isError(err) ? err.message : 'Could not remove the document. Please try again.')
                )
              }
            >
              <X className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      ))}
      {pendingReceipts.map((file, index) => (
        <div key={`${file.name}:${file.lastModified}:${index}`} className="flex items-center gap-2">
          <Badge variant="outline">{file.name}</Badge>
          <span className="text-xs text-muted-foreground">Uploads on save</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => onRemovePendingReceipt?.(index)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  )
}

export function PaymentStepFields({
  values,
  errors,
  linkedEventCount,
  itemCount,
  externalBankAccounts,
  klearFinancialAccounts,
  klearFinancialAccount,
  destinationLocked = false,
  onFieldChange,
  onFormUpdate,
  receipts,
  onUploadReceipts,
  onRemoveReceipt,
  pendingReceipts,
  onAddPendingReceipts,
  onRemovePendingReceipt,
  getReceiptDownloadUrl,
  createBankAccountAction
}: PaymentStepFieldsProps) {
  const networkOptions = PaymentNetworkOptions.filter((network) => network.value !== PAYMENT_NETWORK_KLEAR_TO_KLEAR)

  const destinationOptions = supplierAccountsForNetwork(
    externalBankAccounts,
    values.paymentNetwork as SupplierPaymentNetwork
  ).map((account) => ({
    label: account.nameAndLast4,
    value: toString(account.id)
  }))

  const sourceAccounts =
    klearFinancialAccounts && klearFinancialAccounts.length > 0
      ? klearFinancialAccounts.filter((account) => account.isOpen)
      : klearFinancialAccount?.isOpen
        ? [klearFinancialAccount]
        : []

  const daysUntilPayment =
    values.effectiveDate && values.effectiveDate.isAfter(KlearDate.today())
      ? values.effectiveDate.diff(KlearDate.today(), 'day')
      : null
  const scheduledLabel =
    daysUntilPayment != null ? `${daysUntilPayment} ${daysUntilPayment === 1 ? 'day' : 'days'}` : null

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm font-medium">Invoice Number</Label>
          <Input value={values.number} onChange={(e) => onFieldChange('number', e.target.value)} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <MoneyInput
          value={values.amount}
          onChange={noop}
          disabled
          label="Amount"
          helperText={amountHelperText(linkedEventCount, itemCount)}
          errorMessage={errors.amount}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm font-medium">Payment Date</Label>
          <DatePicker value={values.effectiveDate} onChange={(date) => date && onFieldChange('effectiveDate', date)} />
        </div>
        {scheduledLabel ? (
          <div className="space-y-2">
            <Label className="text-sm font-medium">Payment will go out in (UTC time)</Label>
            <Input value={scheduledLabel} disabled />
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm font-medium">Source Account</Label>
          <Select
            value={values.sourceAccountId != null ? toString(values.sourceAccountId) : undefined}
            onValueChange={(accountId) => {
              const id = toInteger(accountId)
              const selected = sourceAccounts.find((account) => account.id === id)
              if (!selected?.id) return
              onFormUpdate((draft) => {
                draft.sourceAccountId = selected.id!
                draft.sourceAccountOrganizationId = selected.organization.id
                draft.sourceAccountOrganizationName = selected.organization.name
                draft.sourceAccountType = PAYMENT_ACCOUNT_TYPE_KLEAR_FINANCIAL_ACCOUNT
              })
            }}
          >
            <SelectTrigger className="w-full" aria-label="Source Account" aria-invalid={errors.sourceAccountId != null}>
              <SelectValue placeholder="Select source account…" />
            </SelectTrigger>
            <SelectContent>
              {sourceAccounts
                .filter((account) => account.id != null)
                .map((account) => (
                  <SelectItem key={account.id} value={toString(account.id)}>
                    {klearAccountOptionLabel(account)}
                    {account.account ? ` · ${account.balanceAvailable.toString()}` : ''}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <FieldError message={errors.sourceAccountId} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm font-medium">Network</Label>
          <Combobox
            value={values.paymentNetwork}
            options={networkOptions}
            placeholder="Search networks…"
            triggerLabel="Select network…"
            onChange={(network) => {
              if (!network) return
              onFormUpdate((draft) => {
                draft.paymentNetwork = network as PaymentNetwork
                const selectedDestination = externalBankAccounts.find(
                  (account) => account.id === draft.destinationAccountId
                )
                if (
                  selectedDestination &&
                  !selectedDestination.supportedPaymentNetworks.includes(
                    network as typeof PAYMENT_NETWORK_ACH | typeof PAYMENT_NETWORK_WIRE
                  )
                ) {
                  draft.destinationAccountId = null
                  draft.destinationAccountOrganizationId = null
                  draft.destinationAccountOrganizationName = ''
                }
              })
            }}
            error={errors.paymentNetwork ? { message: errors.paymentNetwork } : undefined}
          />
          <FieldError message={errors.paymentNetwork} />
        </div>
      </div>

      {values.paymentNetwork === PAYMENT_NETWORK_ACH || values.paymentNetwork === PAYMENT_NETWORK_WIRE ? (
        <PaymentReferenceInput
          network={values.paymentNetwork as BankPaymentNetwork}
          value={values.statementDescriptor}
          onChange={(value) => onFieldChange('statementDescriptor', value)}
          error={errors.statementDescriptor}
        />
      ) : null}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm font-medium">Destination Account</Label>
          <Combobox
            value={values.destinationAccountId != null ? toString(values.destinationAccountId) : ''}
            options={destinationOptions}
            disabled={destinationLocked}
            placeholder="Search accounts…"
            triggerLabel="Select destination account…"
            onChange={(accountId) => {
              if (accountId === '') return
              const id = isNumber(accountId) ? accountId : toInteger(accountId)
              const selectedAccount = externalBankAccounts.find((account) => account.id === id)
              if (!selectedAccount) return
              onFormUpdate((draft) => {
                draft.destinationAccountId = selectedAccount.id ?? null
                draft.destinationAccountOrganizationId = selectedAccount.organizationId
                draft.destinationAccountOrganizationName = ''
                draft.destinationAccountType = PAYMENT_ACCOUNT_TYPE_EXTERNAL_FINANCIAL_ACCOUNT
              })
            }}
            error={errors.destinationAccountId ? { message: errors.destinationAccountId } : undefined}
          />
          <FieldError message={errors.destinationAccountId} />
          {!destinationLocked ? createBankAccountAction : null}
        </div>
      </div>

      {values.paymentNetwork === PAYMENT_NETWORK_ACH || values.paymentNetwork === PAYMENT_NETWORK_WIRE ? (
        <div className="space-y-1">
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer select-none">
            <Switch
              checked={values.notifySupplier}
              onCheckedChange={(checked) => onFieldChange('notifySupplier', checked)}
            />
            Email the supplier when this payment is sent
          </label>
          <p className="text-sm text-muted-foreground">
            Goes to the recipient email on the supplier&apos;s bank account, with the amount, date and reference.
          </p>
        </div>
      ) : null}

      {onUploadReceipts ? (
        <ReceiptUploadField
          receipts={receipts ?? []}
          pendingReceipts={pendingReceipts ?? []}
          onUploadReceipts={onUploadReceipts}
          onRemoveReceipt={onRemoveReceipt}
          onAddPendingReceipts={onAddPendingReceipts}
          onRemovePendingReceipt={onRemovePendingReceipt}
          getReceiptDownloadUrl={getReceiptDownloadUrl}
        />
      ) : null}

      <div className="space-y-2">
        <Label className="text-sm font-medium">Notes</Label>
        <Textarea value={values.notes} onChange={(e) => onFieldChange('notes', e.target.value)} />
      </div>
    </div>
  )
}
